import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Laufzeit, LeistungsVerlauf, PhaseArt, PHASE_NAME, StandPrognose,
    datumAnzeige, isoTag, leistungJeMonat, protokollStandAm, tagAnteil,
} from './projektStand';
import { tageBis } from './projectDataHygiene';

/**
 * Leistung ueber die Laufzeit — die Kurve im Projektstand
 * (docs/projektstand-hub-plan.md). Handgezeichnetes SVG wie RevenueChart im
 * Cockpit, kein Chart-Framework.
 *
 * Zwei Modi auf derselben Flaeche:
 *  · VERLAUF — Erbracht (emerald, Treppe mit Flaeche) gegen Gleichmaessig
 *    (blau gestrichelt), dazu die PROGNOSE (emerald gepunktet) vom heutigen
 *    Stand bis zum Ende bei gleichem Tempo. Am Heute-Punkt pulsiert ein Ring.
 *  · MONATE — Zuwachs je Monat als Saeulen, dazu die Linie „noetig pro
 *    Monat". Leere Monate bleiben stehen: gerade die Luecke ist die Aussage.
 *
 * FESTE GROESSE: das Diagramm fuellt den Rahmen der Karte (Breite und Hoehe
 * per ResizeObserver) und waechst nie mit den Daten. Beim Erscheinen zeichnet
 * sich die Linie, danach folgt die Prognose; Saeulen wachsen gestaffelt. Bei
 * „weniger Bewegung" steht alles sofort, der Puls entfaellt.
 */

const ERBRACHT = 'var(--color-emerald-500, #0d9f72)';
const ERBRACHT_HELL = 'var(--color-emerald-300, #6ee7b7)';
const PLAN = 'var(--color-blue-500, #2f83f6)';
const RASTER = 'var(--color-slate-100, #f1f5f9)';
const GRUNDLINIE = 'var(--color-slate-200, #e4e9f0)';
const HEUTE = 'var(--color-slate-400, #6a7686)';
const PHASE_FLAECHE = 'var(--color-slate-50, #f8fafc)';

const PAD = { t: 18, r: 44, b: 20, l: 36 };
const MONATSNAMEN = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

const bewegungReduziert = (): boolean =>
    typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** YYYY-MM-DD + n Tage, ohne Zeitzone. */
const tagPlus = (tag: string, n: number): string => {
    const [y, m, d] = tag.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
};

const prozentText = (p: number, stellen = 0) =>
    `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: stellen, minimumFractionDigits: stellen }).format(p)} %`;

/** Runde Achsenobergrenze: 5, 10, 20, 25, 50 … */
const schoeneGrenze = (max: number): number => {
    const stufen = [2, 4, 5, 8, 10, 15, 20, 25, 30, 40, 50, 75, 100];
    return stufen.find(s => s >= max) ?? Math.ceil(max / 50) * 50;
};

export type VerlaufModus = 'verlauf' | 'monate';

interface Props {
    verlauf: LeistungsVerlauf;
    laufzeit: Laufzeit;
    prognose: StandPrognose;
    modus: VerlaufModus;
}

export const LeistungsVerlaufChart: React.FC<Props> = ({ verlauf, laufzeit, prognose, modus }) => {
    const rahmen = useRef<HTMLDivElement>(null);
    const [mass, setMass] = useState({ breite: 0, hoehe: 0 });
    useEffect(() => {
        const el = rahmen.current;
        if (!el) return;
        const messen = (b: number, h: number) => setMass(m => (m.breite === b && m.hoehe === h ? m : { breite: b, hoehe: h }));
        if (typeof ResizeObserver === 'undefined') {
            messen(el.clientWidth, el.clientHeight);
            return;
        }
        const ro = new ResizeObserver(e => messen(e[0].contentRect.width, e[0].contentRect.height));
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // Einzeichnen: erst leer malen, im naechsten Frame animieren. Die Karte
    // haengt das Diagramm per key an Ansicht und Modus — ein Wechsel zeichnet neu.
    const [gezeichnet, setGezeichnet] = useState(false);
    const bereit = mass.breite > 0;
    useEffect(() => {
        if (!bereit) return;
        const id = requestAnimationFrame(() => setGezeichnet(true));
        return () => cancelAnimationFrame(id);
    }, [bereit]);
    const ruhig = bewegungReduziert();
    const sichtbar = gezeichnet || ruhig;

    const [hoverTag, setHoverTag] = useState<string | null>(null);
    const [hoverMonat, setHoverMonat] = useState<number | null>(null);

    const { breite, hoehe } = mass;
    const cw = Math.max(0, breite - PAD.l - PAD.r);
    const ch = Math.max(0, hoehe - PAD.t - PAD.b);
    const { von, bis, punkte, soll, heute, endProzent, maxProzent } = verlauf;

    const uebergang = (eigenschaft: string, ms: number, verzoegerung = 0) =>
        ruhig ? undefined : `${eigenschaft} ${ms}ms cubic-bezier(0.22, 1, 0.36, 1) ${verzoegerung}ms`;

    const monate = useMemo(() => leistungJeMonat(verlauf), [verlauf]);

    // ─── Legende ────────────────────────────────────────────────────────
    const legende = (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-400">
            {modus === 'verlauf' ? (
                <>
                    <span className="flex items-center gap-1.5">
                        <svg width="14" height="4" aria-hidden="true"><line x1="0" y1="2" x2="14" y2="2" stroke={ERBRACHT} strokeWidth="2" strokeLinecap="round" /></svg>
                        Erbracht
                    </span>
                    {soll && (
                        <span className="flex items-center gap-1.5">
                            <svg width="14" height="4" aria-hidden="true"><line x1="0" y1="2" x2="14" y2="2" stroke={PLAN} strokeWidth="1.5" strokeDasharray="4 3" /></svg>
                            Gleichmäßig
                        </span>
                    )}
                    {prognose.prognoseEnde !== null && (
                        <span className="flex items-center gap-1.5">
                            <svg width="14" height="4" aria-hidden="true"><line x1="1" y1="2" x2="14" y2="2" stroke={ERBRACHT} strokeWidth="2" strokeDasharray="0.1 4" strokeLinecap="round" /></svg>
                            Prognose
                        </span>
                    )}
                </>
            ) : (
                <>
                    <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: ERBRACHT }} /> Leistung je Monat</span>
                    {prognose.noetigProMonat !== null && (
                        <span className="flex items-center gap-1.5">
                            <svg width="14" height="4" aria-hidden="true"><line x1="0" y1="2" x2="14" y2="2" stroke={PLAN} strokeWidth="1.5" strokeDasharray="4 3" /></svg>
                            nötig bis Ende
                        </span>
                    )}
                </>
            )}
        </div>
    );

    // ─── Modus VERLAUF ──────────────────────────────────────────────────
    const zeichneVerlauf = () => {
        const obergrenze = Math.max(maxProzent, Math.min(prognose.prognoseEnde ?? 0, 200));
        const yMax = Math.ceil(obergrenze / 25) * 25;
        const x = (tag: string) => PAD.l + tagAnteil(von, bis, tag) * cw;
        const y = (p: number) => PAD.t + ch - (Math.max(0, Math.min(yMax, p)) / yMax) * ch;

        const endeTag = heute ?? punkte[punkte.length - 1]?.tag ?? bis;
        const linie: [number, number][] = [[x(von), y(0)]];
        let stand = 0;
        for (const p of punkte) {
            linie.push([x(p.tag), y(stand)], [x(p.tag), y(p.prozent)]);
            stand = p.prozent;
        }
        linie.push([x(endeTag), y(stand)]);
        if (Math.abs(endProzent - stand) > 0.05) linie.push([x(endeTag), y(endProzent)]);
        const [endX, endY] = linie[linie.length - 1];
        const linienPfad = linie.map(([a, b], i) => `${i ? 'L' : 'M'}${a.toFixed(1)},${b.toFixed(1)}`).join(' ');
        const flaechenPfad = `${linienPfad} L${endX.toFixed(1)},${y(0).toFixed(1)} Z`;
        const ticks = [0, 50, 100, ...(yMax > 100 ? [yMax] : [])];

        const phasen = laufzeit.abschnitte
            .map(a => {
                const pv = isoTag(a.vonTag);
                const pb = isoTag(a.bisTag);
                return pv && pb ? { art: a.art, x1: x(pv), x2: x(pb) } : null;
            })
            .filter((p): p is { art: PhaseArt; x1: number; x2: number } => p !== null && p.x2 > p.x1);

        const planAm = (tag: string): number | null => {
            if (!soll) return null;
            if (tag <= soll.von) return 0;
            if (tag >= soll.bis) return 100;
            return tagAnteil(soll.von, soll.bis, tag) * 100;
        };
        // Am Ende der Linie gilt der Zaehlerstand (dort macht sie ihren
        // Abgleichschritt) — auch ohne Heute-Marke (beendetes Projekt), sonst
        // zeigte der Tooltip am Endpunkt den Protokollstand.
        const erbrachtAm = (tag: string): number | null => {
            if (heute && tag > heute) return null;
            if (tag >= endeTag) return endProzent;
            return protokollStandAm(punkte, tag);
        };
        const prognoseAm = (tag: string): number | null => {
            if (!heute || prognose.prognoseEnde === null || !prognose.prognoseTag || tag <= heute || tag > prognose.prognoseTag) return null;
            const t = tagAnteil(heute, prognose.prognoseTag, tag);
            return endProzent + (prognose.prognoseEnde - endProzent) * t;
        };

        const gesamtTage = tageBis(von, bis) ?? 0;
        const beiPointer = (e: React.PointerEvent<SVGRectElement>) => {
            const box = e.currentTarget.getBoundingClientRect();
            const anteil = cw > 0 ? Math.max(0, Math.min(1, (e.clientX - box.left) / cw)) : 0;
            setHoverTag(tagPlus(von, Math.round(anteil * gesamtTage)));
        };
        const hover = hoverTag ? { tag: hoverTag, erbracht: erbrachtAm(hoverTag), plan: planAm(hoverTag), prognose: prognoseAm(hoverTag) } : null;

        const mitPrognose = heute !== null && prognose.prognoseEnde !== null && prognose.prognoseTag !== null;
        const progX = mitPrognose ? x(prognose.prognoseTag!) : 0;
        const progY = mitPrognose ? y(prognose.prognoseEnde!) : 0;

        return (
            <>
                <svg width={breite} height={hoehe} role="img" aria-label={`Erbrachte Leistung ${prozentText(endProzent)}. Zeitraum ${datumAnzeige(von)} bis ${datumAnzeige(bis)}.`} className="absolute inset-0 block select-none overflow-visible">
                    {phasen.map(p => (
                        <g key={p.art}>
                            {p.art === 'planung' && <rect x={p.x1} y={PAD.t} width={p.x2 - p.x1} height={ch} fill={PHASE_FLAECHE} />}
                            {p.x1 > PAD.l + 0.5 && <line x1={p.x1} y1={PAD.t} x2={p.x1} y2={PAD.t + ch} stroke={GRUNDLINIE} strokeWidth="1" />}
                            {p.x2 - p.x1 > 72 && <text x={p.x1 + 6} y={PAD.t + 13} className="text-[11px] fill-slate-400">{PHASE_NAME[p.art]}</text>}
                        </g>
                    ))}
                    {ticks.map(t => (
                        <g key={t}>
                            <line x1={PAD.l} y1={y(t)} x2={PAD.l + cw} y2={y(t)} stroke={t === 0 ? GRUNDLINIE : RASTER} strokeWidth="1" />
                            <text x={PAD.l - 6} y={y(t) + 4} textAnchor="end" className="text-[11px] fill-slate-400" style={{ fontVariantNumeric: 'tabular-nums' }}>{t} %</text>
                        </g>
                    ))}
                    <text x={PAD.l} y={hoehe - 3} className="text-[11px] fill-slate-400">{datumAnzeige(von)}</text>
                    <text x={PAD.l + cw} y={hoehe - 3} textAnchor="end" className="text-[11px] fill-slate-400">{datumAnzeige(bis)}</text>

                    {soll && (
                        <polyline
                            points={`${x(soll.von)},${y(0)} ${x(soll.bis)},${y(100)}${x(soll.bis) < PAD.l + cw - 0.5 ? ` ${PAD.l + cw},${y(100)}` : ''}`}
                            fill="none" stroke={PLAN} strokeWidth="1.5" strokeDasharray="5 4" strokeLinecap="round" strokeLinejoin="round"
                            style={{ opacity: sichtbar ? 1 : 0, transition: uebergang('opacity', 500) }}
                        />
                    )}

                    {heute && (
                        <g>
                            <line x1={x(heute)} y1={PAD.t - 4} x2={x(heute)} y2={PAD.t + ch} stroke={HEUTE} strokeWidth="1" strokeOpacity="0.5" />
                            <text x={x(heute)} y={PAD.t - 7} textAnchor={x(heute) > PAD.l + cw - 30 ? 'end' : x(heute) < PAD.l + 30 ? 'start' : 'middle'} className="text-[11px] fill-slate-400">heute</text>
                        </g>
                    )}

                    <path d={flaechenPfad} fill={ERBRACHT} style={{ opacity: sichtbar ? 0.09 : 0, transition: uebergang('opacity', 700, 500) }} />
                    <path
                        d={linienPfad}
                        fill="none" stroke={ERBRACHT} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"
                        pathLength={1} strokeDasharray="1 1"
                        style={{ strokeDashoffset: sichtbar ? 0 : 1, transition: uebergang('stroke-dashoffset', 1200) }}
                    />

                    {/* Prognose: gepunktet vom heutigen Stand bis zum Ende — erst nach der Linie. */}
                    {mitPrognose && (
                        <g style={{ opacity: sichtbar ? 1 : 0, transition: uebergang('opacity', 500, 1150) }}>
                            <line x1={endX} y1={endY} x2={progX} y2={progY} stroke={ERBRACHT} strokeWidth="2" strokeDasharray="0.1 5" strokeLinecap="round" />
                            <circle cx={progX} cy={progY} r="3" fill="#ffffff" stroke={ERBRACHT} strokeWidth="1.5" />
                            <text
                                x={progX > PAD.l + cw - 4 ? progX + 7 : progX}
                                y={progX > PAD.l + cw - 4 ? progY + 4 : progY - 8}
                                textAnchor={progX > PAD.l + cw - 4 ? 'start' : 'middle'}
                                className="text-[11px] fill-slate-500"
                            >
                                {prozentText(prognose.prognoseEnde!)}
                            </text>
                        </g>
                    )}

                    <g style={{ opacity: sichtbar ? 1 : 0, transition: uebergang('opacity', 300, 1000) }}>
                        {/* Puls am Heute-Punkt — leise, langsam, bei „weniger Bewegung" aus. */}
                        {heute && !ruhig && (
                            <circle cx={endX} cy={endY} r="4" fill={ERBRACHT} opacity="0.35">
                                <animate attributeName="r" values="4;13" dur="2.4s" repeatCount="indefinite" />
                                <animate attributeName="opacity" values="0.35;0" dur="2.4s" repeatCount="indefinite" />
                            </circle>
                        )}
                        <circle cx={endX} cy={endY} r="5.5" fill="#ffffff" />
                        <circle cx={endX} cy={endY} r="3.5" fill={ERBRACHT} />
                        {!mitPrognose && <text x={endX + 8} y={endY + 4} className="text-[11px] font-semibold fill-slate-600">{prozentText(endProzent)}</text>}
                    </g>

                    {hover && (
                        <g className="pointer-events-none">
                            <line x1={x(hover.tag)} y1={PAD.t} x2={x(hover.tag)} y2={PAD.t + ch} stroke={GRUNDLINIE} strokeWidth="1" />
                            {hover.plan !== null && <><circle cx={x(hover.tag)} cy={y(hover.plan)} r="5.5" fill="#ffffff" /><circle cx={x(hover.tag)} cy={y(hover.plan)} r="3.5" fill={PLAN} /></>}
                            {hover.erbracht !== null && <><circle cx={x(hover.tag)} cy={y(hover.erbracht)} r="5.5" fill="#ffffff" /><circle cx={x(hover.tag)} cy={y(hover.erbracht)} r="3.5" fill={ERBRACHT} /></>}
                            {hover.prognose !== null && <><circle cx={x(hover.tag)} cy={y(hover.prognose)} r="5.5" fill="#ffffff" /><circle cx={x(hover.tag)} cy={y(hover.prognose)} r="3" fill="#ffffff" stroke={ERBRACHT} strokeWidth="1.5" /></>}
                        </g>
                    )}
                    <rect x={PAD.l} y={PAD.t} width={cw} height={ch} fill="transparent" onPointerMove={beiPointer} onPointerDown={beiPointer} onPointerLeave={() => setHoverTag(null)} />
                </svg>

                {hover && (
                    <Tooltip links={x(hover.tag)} breite={breite} titel={datumAnzeige(hover.tag)} zeilen={[
                        hover.erbracht !== null ? { name: 'Erbracht', wert: prozentText(hover.erbracht), schluessel: <LinienSchluessel farbe={ERBRACHT} /> } : null,
                        hover.prognose !== null ? { name: 'Prognose', wert: prozentText(hover.prognose), schluessel: <LinienSchluessel farbe={ERBRACHT} gepunktet /> } : null,
                        hover.plan !== null ? { name: 'Gleichmäßig', wert: prozentText(hover.plan), schluessel: <LinienSchluessel farbe={PLAN} gestrichelt /> } : null,
                    ]} />
                )}
            </>
        );
    };

    // ─── Modus MONATE ───────────────────────────────────────────────────
    const zeichneMonate = () => {
        const liste = monate.monate;
        const noetig = prognose.noetigProMonat;
        const yMax = schoeneGrenze(Math.max(1, ...liste.map(m => m.prozent), noetig ?? 0) * 1.1);
        const y = (p: number) => PAD.t + ch - (Math.max(0, Math.min(yMax, p)) / yMax) * ch;
        const band = liste.length > 0 ? cw / liste.length : cw;
        const saeule = Math.max(3, Math.min(24, band * 0.62));
        const jeder = Math.max(1, Math.ceil(liste.length / Math.max(1, Math.floor(cw / 34))));
        const aktuell = heute?.slice(0, 7);
        const ticks = [0, yMax / 2, yMax];

        return (
            <>
                <svg width={breite} height={hoehe} role="img" aria-label="Leistung je Monat" className="absolute inset-0 block select-none overflow-visible">
                    {ticks.map(t => (
                        <g key={t}>
                            <line x1={PAD.l} y1={y(t)} x2={PAD.l + cw} y2={y(t)} stroke={t === 0 ? GRUNDLINIE : RASTER} strokeWidth="1" />
                            <text x={PAD.l - 6} y={y(t) + 4} textAnchor="end" className="text-[11px] fill-slate-400" style={{ fontVariantNumeric: 'tabular-nums' }}>{prozentText(t, Number.isInteger(t) ? 0 : 1)}</text>
                        </g>
                    ))}
                    {liste.map((m, i) => {
                        const mitte = PAD.l + band * i + band / 2;
                        const h = Math.max(0, y(0) - y(m.prozent));
                        const laufend = m.monat === aktuell;
                        const [jahr, mon] = m.monat.split('-').map(Number);
                        return (
                            <g key={m.monat}>
                                {hoverMonat === i && <rect x={mitte - band / 2 + 1} y={PAD.t} width={band - 2} height={ch} fill={PHASE_FLAECHE} rx="3" />}
                                {/* Saeule: oben gerundet, an der Grundlinie gerade. Waechst gestaffelt von unten. */}
                                {h > 0 && (
                                    <g
                                        fill={laufend ? ERBRACHT_HELL : ERBRACHT}
                                        style={{
                                            transformBox: 'fill-box', transformOrigin: 'bottom',
                                            transform: sichtbar ? 'scaleY(1)' : 'scaleY(0)',
                                            transition: uebergang('transform', 700, i * 35),
                                        }}
                                    >
                                        <rect x={mitte - saeule / 2} y={y(m.prozent)} width={saeule} height={h} rx={Math.min(3, h / 2)} />
                                        <rect x={mitte - saeule / 2} y={y(0) - Math.min(3, h / 2)} width={saeule} height={Math.min(3, h / 2)} />
                                    </g>
                                )}
                                {(i % jeder === 0 || i === liste.length - 1) && (
                                    <text x={mitte} y={hoehe - 3} textAnchor="middle" className="text-[11px] fill-slate-400">
                                        {mon === 1 || i === 0 ? `${MONATSNAMEN[mon - 1]} ${String(jahr).slice(2)}` : MONATSNAMEN[mon - 1]}
                                    </text>
                                )}
                                <rect x={mitte - band / 2} y={PAD.t} width={band} height={ch} fill="transparent" onPointerEnter={() => setHoverMonat(i)} onPointerDown={() => setHoverMonat(i)} onPointerLeave={() => setHoverMonat(null)} />
                            </g>
                        );
                    })}
                    {noetig !== null && (
                        <g className="pointer-events-none" style={{ opacity: sichtbar ? 1 : 0, transition: uebergang('opacity', 500, 400) }}>
                            <line x1={PAD.l} y1={y(noetig)} x2={PAD.l + cw} y2={y(noetig)} stroke={PLAN} strokeWidth="1.5" strokeDasharray="5 4" />
                            <text x={PAD.l + cw + 4} y={y(noetig) + 4} className="text-[11px] fill-slate-500">{prozentText(noetig, 1)}</text>
                        </g>
                    )}
                    {Math.abs(monate.ohneDatum) >= 1 && (
                        <text x={PAD.l + 4} y={PAD.t + 12} className="text-[11px] fill-slate-400">{monate.ohneDatum > 0 ? '+' : ''}{prozentText(monate.ohneDatum)} ohne Buchungsdatum</text>
                    )}
                </svg>
                {hoverMonat !== null && liste[hoverMonat] && (() => {
                    const m = liste[hoverMonat];
                    const [jahr, mon] = m.monat.split('-').map(Number);
                    return (
                        <Tooltip links={PAD.l + band * hoverMonat + band / 2} breite={breite} titel={`${MONATSNAMEN[mon - 1]} ${jahr}${m.monat === aktuell ? ' (laufend)' : ''}`} zeilen={[
                            { name: 'Leistung', wert: prozentText(m.prozent, 1), schluessel: <span className="w-2.5 h-2.5 rounded-sm" style={{ background: ERBRACHT }} /> },
                            noetig !== null ? { name: 'nötig', wert: prozentText(noetig, 1), schluessel: <LinienSchluessel farbe={PLAN} gestrichelt /> } : null,
                        ]} />
                    );
                })()}
            </>
        );
    };

    return (
        <div className="h-full flex flex-col">
            <div className="mb-1 shrink-0">{legende}</div>
            <div ref={rahmen} className="relative w-full flex-1 min-h-0">
                {breite > 0 && hoehe > 0 && (modus === 'verlauf' ? zeichneVerlauf() : zeichneMonate())}
            </div>
        </div>
    );
};

const LinienSchluessel: React.FC<{ farbe: string; gestrichelt?: boolean; gepunktet?: boolean }> = ({ farbe, gestrichelt, gepunktet }) => (
    <svg width="12" height="4" aria-hidden="true">
        <line x1="1" y1="2" x2="12" y2="2" stroke={farbe} strokeWidth="2" strokeLinecap="round" strokeDasharray={gepunktet ? '0.1 3.5' : gestrichelt ? '4 2' : undefined} />
    </svg>
);

const Tooltip: React.FC<{
    links: number; breite: number; titel: string;
    zeilen: ({ name: string; wert: string; schluessel: React.ReactNode } | null)[];
}> = ({ links, breite, titel, zeilen }) => (
    <div
        className="absolute z-10 pointer-events-none bg-white border border-slate-200 rounded-lg shadow-lg px-2.5 py-1.5 text-[11px] min-w-[150px] animate-fade-in"
        style={{ top: PAD.t, left: Math.min(Math.max(0, links + 12), Math.max(0, breite - 160)) }}
    >
        <div className="text-slate-400 mb-0.5">{titel}</div>
        {zeilen.filter((z): z is NonNullable<typeof z> => z !== null).map(z => (
            <div key={z.name} className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-1.5 text-slate-500">{z.schluessel}{z.name}</span>
                <span className="font-semibold text-slate-800">{z.wert}</span>
            </div>
        ))}
    </div>
);
